import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, formatDate, formatQuantity } from "@/lib/format";
import { useEffect } from "react";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { collapseForCustomer, type MaterialDisplayMode } from "@/lib/calc";
import {
  useCompanyProfile,
  formatOrgNumber,
  formatPhone,
  formatWebsite,
} from "@/hooks/use-company-profile";
import { tryGetModule, type ModuleKey } from "@/modules";

export const Route = createFileRoute("/_authenticated/quotes/$id_/pdf")({
  component: QuotePdfView,
});

type QuoteItem = {
  item_type: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  is_hidden_on_quote: boolean;
  customer_note?: string | null;
};

type QuoteImage = {
  id: string;
  url: string;
  caption: string | null;
};

const LABOR_TYPES = new Set(["labor", "service_vehicle", "travel", "lift", "documentation"]);
const MATERIAL_TYPES = new Set(["material", "small_parts"]);

function groupItems(items: QuoteItem[]) {
  return {
    labor: items.filter((i) => LABOR_TYPES.has(i.item_type)),
    materials: items.filter((i) => MATERIAL_TYPES.has(i.item_type)),
    options: items.filter((i) => i.item_type === "option" || i.item_type === "addon"),
    discounts: items.filter((i) => i.item_type === "discount"),
    other: items.filter(
      (i) =>
        !LABOR_TYPES.has(i.item_type) &&
        !MATERIAL_TYPES.has(i.item_type) &&
        i.item_type !== "option" &&
        i.item_type !== "addon" &&
        i.item_type !== "discount",
    ),
  };
}

function QuotePdfView() {
  const { id } = Route.useParams();
  const { data: company } = useCompanyProfile();

  const { data } = useQuery({
    queryKey: ["quote-pdf", id],
    queryFn: async () => {
      const [q, its] = await Promise.all([
        supabase
          .from("quotes")
          // Kundevendt PDF: hent aldri total_cost, gross_margin(_percent) eller
          // innkjøpsrabatt (discount_percent) – heller ikke som ubrukt felt.
          .select(
            "id, quote_number, revision_label, title, description, created_at, valid_until, " +
              "customer_summary, terms_override, terms_included, terms_excluded, terms_reservations, terms_payment, " +
              "notes, use_cover_page, module_key, " +
              "presentation, material_display_mode, vat_percent, vat_amount, subtotal_excl_vat, total_incl_vat, " +
              "project_id, include_images, included_image_ids, customers(*), " +
              "projects(address, postal_code, city, contact_name, contact_phone)",
          )
          .eq("id", id)
          .single(),

        supabase
          .from("quote_items")
          // Kundevendt PDF: hent aldri kost-/margindata (unit_cost) til klienten.
          .select("item_type, description, quantity, unit, unit_price, is_hidden_on_quote, customer_note, sort_order")
          .eq("quote_id", id)
          .order("sort_order"),
      ]);
      if (q.error) throw q.error;
      const quote = q.data as unknown as Record<string, unknown> & {
        project_id: string | null;
        include_images: boolean;
        included_image_ids: string[] | null;
      };

      let images: QuoteImage[] = [];
      if (quote.include_images && quote.project_id) {
        const { data: imgs } = await supabase
          .from("project_images")
          .select("id, storage_path, caption")
          .eq("project_id", quote.project_id)
          .order("created_at");
        const selected = (imgs ?? []).filter((row) => {
          const ids = quote.included_image_ids ?? [];
          if (!ids || ids.length === 0) return true; // ingen valgt = ta med alle
          return ids.includes(row.id);
        });
        const signed = await Promise.all(
          selected.map(async (row) => {
            const { data: s } = await supabase.storage
              .from("project-images")
              .createSignedUrl(row.storage_path, 60 * 60);
            return s?.signedUrl
              ? { id: row.id, url: s.signedUrl, caption: row.caption }
              : null;
          }),
        );
        images = signed.filter((x): x is QuoteImage => x !== null);
      }

      return { quote, items: its.data ?? [], images };
    },
  });

  useEffect(() => {
    if (data && company) setTimeout(() => window.print(), 500);
  }, [data, company]);

  if (!data || !company) return <div className="p-10 text-center text-muted-foreground">Laster…</div>;
  const { quote, items, images } = data as unknown as {
    quote: Record<string, unknown> & {
      quote_number: string;
      revision_label: string | null;
      title: string;
      description: string | null;
      created_at: string;
      valid_until: string | null;
      customer_summary: string | null;
      terms_override: string | null;
      terms_included: string | null;
      terms_excluded: string | null;
      terms_reservations: string | null;
      terms_payment: string | null;
      notes: string | null;
      use_cover_page: boolean | null;
      module_key: string | null;
      material_display_mode: MaterialDisplayMode;
      presentation?: "fixed_price" | "itemized" | null;
      vat_percent: number;
      vat_amount: number;
      subtotal_excl_vat: number;
      total_incl_vat: number;
      customers: {
        name: string;
        address?: string;
        postal_code?: string;
        city?: string;
        email?: string;
        phone?: string;
      } | null;
      projects: {
        address?: string | null;
        postal_code?: string | null;
        city?: string | null;
        contact_name?: string | null;
        contact_phone?: string | null;
      } | null;
    };
    items: Array<Record<string, unknown>>;
    images: QuoteImage[];
  };
  const customer = quote.customers;
  const site = quote.projects;
  const hasSite = Boolean(site?.address || site?.postal_code || site?.city);
  const quoteDocLabel =
    tryGetModule(quote.module_key as ModuleKey)?.documentTemplates.find((t) => t.key === "quote")
      ?.label ?? "Tilbud";

  const mode = (quote.material_display_mode ?? "collapse_small") as MaterialDisplayMode;
  const visible = collapseForCustomer(
    items.map((i) => ({
      item_type: i.item_type as string,
      description: i.description as string,
      quantity: Number(i.quantity),
      unit: i.unit as string,
      unit_cost: 0,
      unit_price: Number(i.unit_price),
      is_hidden_on_quote: Boolean(i.is_hidden_on_quote),
      customer_note: (i.customer_note as string | null | undefined) ?? null,
    })),
    mode,
  );

  const grouped = groupItems(visible.map((v) => v.it as QuoteItem));

  const laborSum = grouped.labor.reduce((s, it) => s + Number(it.quantity) * Number(it.unit_price), 0);
  const materialSum = grouped.materials.reduce((s, it) => s + Number(it.quantity) * Number(it.unit_price), 0);
  const otherSum = grouped.other.reduce((s, it) => s + Number(it.quantity) * Number(it.unit_price), 0);

  const fixedPrice = (quote.presentation ?? "itemized") === "fixed_price";

  const useCover = quote.use_cover_page ?? company.pdf_cover_page ?? false;
  // Bolk A4: tekstblokker fra tilbudet. Tomme seksjoner rendres ikke i det hele tatt.
  const termsBlocks = (
    [
      ["Dette er inkludert", quote.terms_included],
      ["Dette er ikke inkludert", quote.terms_excluded],
      ["Forbehold", quote.terms_reservations],
      ["Betalingsbetingelser og fremdrift", quote.terms_payment],
    ] as const
  )
    .map(([title, body]) => ({ title, body: (body ?? "").trim() }))
    .filter((b) => b.body.length > 0);
  // Bakoverkompatibelt: gamle tilbud uten tekstblokker beholder generell vilkårstekst.
  const legacyTerms =
    termsBlocks.length > 0
      ? ""
      : quote.terms_override?.trim() || company.pdf_default_terms?.trim() || quote.notes?.trim() || "";
  const accent = company.pdf_accent_color?.trim() || "#111827";
  const footerText = `${company.company_name} • Org.nr ${formatOrgNumber(company.org_number)} • ${formatWebsite(company.website)}`;

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 22mm 18mm 26mm 18mm;
          @bottom-center {
            content: "${footerText.replace(/"/g, '\\"')} — Side " counter(page) " av " counter(pages);
            font-size: 9pt;
            color: #6b7280;
          }
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .page-break { break-before: page; page-break-before: always; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
        .pdf-body { font-feature-settings: "ss01","cv11"; }
        .pdf-accent { color: ${accent}; }
        .pdf-accent-bg { background: ${accent}; }
      `}</style>

      <div className="min-h-screen bg-neutral-100 pdf-body print:bg-white">
        <div className="no-print sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
            <Link to="/quotes/$id" params={{ id }} className="btn btn-ghost">
              <ArrowLeft className="h-4 w-4" /> Tilbake
            </Link>
            <button onClick={() => window.print()} className="btn btn-primary">
              <Printer className="h-4 w-4" /> Skriv ut / lagre PDF
            </button>
          </div>
        </div>

        <article className="mx-auto my-8 max-w-[210mm] bg-white text-neutral-900 shadow-xl print:my-0 print:max-w-none print:shadow-none">
          {useCover && (
            <CoverPage
              company={company}
              quote={quote}
              customer={customer}
              accent={accent}
              docLabel={quoteDocLabel}
            />
          )}

          <div className={`px-14 py-12 print:p-0 ${useCover ? "page-break" : ""}`}>
            {/* Header */}
            <header className="flex items-start justify-between gap-8 border-b-2 pb-8" style={{ borderColor: accent }}>
              <div className="flex items-center gap-4">
                <div
                  className={`grid h-24 w-56 shrink-0 place-items-center overflow-hidden rounded-2xl ${
                    company.logo_url ? "" : "bg-neutral-900"
                  }`}
                >
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.company_name}
                      className="max-h-24 max-w-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <span className="text-xs font-semibold uppercase text-white/70">
                      {(company.company_name ?? "?").slice(0, 2)}
                    </span>
                  )}
                </div>

                <div>
                  <div className="text-lg font-semibold tracking-tight">{company.company_name}</div>
                  <div className="text-xs text-neutral-500">
                    Org.nr {formatOrgNumber(company.org_number)}
                  </div>
                  <div className="text-xs text-neutral-500">
                    Telefon: {formatPhone(company.phone)}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {company.company_email}
                  </div>
                  <div className="text-xs text-neutral-500">{formatWebsite(company.website)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] pdf-accent">
                  {quoteDocLabel}
                </div>
                <div className="mt-1 font-mono text-sm text-neutral-700">
                  {quote.quote_number}
                  {quote.revision_label ? ` · Rev ${quote.revision_label}` : ""}
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-wider text-neutral-400">Dato</div>
                <div className="text-sm text-neutral-700">{formatDate(quote.created_at)}</div>
                {quote.valid_until && (
                  <>
                    <div className="mt-2 text-[11px] uppercase tracking-wider text-neutral-400">Gyldig til</div>
                    <div className="text-sm text-neutral-700">{formatDate(quote.valid_until)}</div>
                  </>
                )}
              </div>
            </header>

            {/* Kunde + prosjekt */}
            <section className="grid grid-cols-2 gap-10 py-10">
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
                  Fakturamottaker
                </div>
                {customer ? (
                  <>
                    <div className="text-lg font-semibold">{customer.name}</div>
                    {customer.address && <div className="mt-1 text-sm text-neutral-600">{customer.address}</div>}
                    {(customer.postal_code || customer.city) && (
                      <div className="text-sm text-neutral-600">
                        {customer.postal_code} {customer.city}
                      </div>
                    )}
                    {customer.email && <div className="mt-2 text-sm text-neutral-500">{customer.email}</div>}
                    {customer.phone && <div className="text-sm text-neutral-500">{customer.phone}</div>}
                  </>
                ) : (
                  <div className="text-sm text-neutral-500">—</div>
                )}
              </div>
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Prosjekt</div>
                <div className="text-lg font-semibold leading-snug">{quote.title}</div>
                {quote.description && (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-600">{quote.description}</p>
                )}
                {hasSite && (
                  <>
                    <div className="mt-4 text-[11px] uppercase tracking-wider text-neutral-400">Arbeidssted</div>
                    {site?.address && <div className="text-sm text-neutral-600">{site.address}</div>}
                    {(site?.postal_code || site?.city) && (
                      <div className="text-sm text-neutral-600">
                        {site?.postal_code} {site?.city}
                      </div>
                    )}
                    {site?.contact_name && (
                      <div className="mt-1 text-sm text-neutral-500">
                        Kontakt: {site.contact_name}
                        {site.contact_phone ? ` · ${site.contact_phone}` : ""}
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* Kundeoppsummering */}
            {quote.customer_summary && (
              <section className="avoid-break mb-8 rounded-2xl p-6" style={{ background: `${accent}0d` }}>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] pdf-accent">
                  Sammendrag
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
                  {quote.customer_summary}
                </p>
              </section>
            )}

            {/* Poster */}
            {fixedPrice ? (
              <FixedPriceSections
                rows={[
                  { title: "Arbeid", sum: laborSum },
                  { title: "Materiell", sum: materialSum },
                  { title: "Øvrige poster", sum: otherSum },
                ].filter((r) => r.sum > 0)}
              />
            ) : (
              <div className="space-y-8">
                {grouped.labor.length > 0 && <ItemSection title="Arbeid" items={grouped.labor} />}
                {grouped.materials.length > 0 && <ItemSection title="Materiell" items={grouped.materials} />}
                {grouped.other.length > 0 && <ItemSection title="Øvrige poster" items={grouped.other} />}
                {grouped.options.length > 0 && <ItemSection title="Opsjoner" items={grouped.options} muted />}
                {grouped.discounts.length > 0 && <ItemSection title="Rabatter" items={grouped.discounts} negative />}
              </div>
            )}

            {/* Sum */}
            <section className="avoid-break mt-10 flex justify-end">
              <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
                {laborSum > 0 && <SumRow label="Arbeid" value={formatCurrency(laborSum)} muted />}
                {materialSum > 0 && <SumRow label="Materiell" value={formatCurrency(materialSum)} muted />}
                {otherSum > 0 && <SumRow label="Andre kostnader" value={formatCurrency(otherSum)} muted />}
                <div className="my-2 border-t border-neutral-200" />
                <SumRow label="Sum eks. mva" value={formatCurrency(quote.subtotal_excl_vat)} />
                <SumRow label={`MVA (${quote.vat_percent}%)`} value={formatCurrency(quote.vat_amount)} muted />
                <div className="my-3 border-t border-neutral-200" />
                <SumRow label="Totalt inkl. mva" value={formatCurrency(quote.total_incl_vat)} large />
              </div>
            </section>

            {/* Bilder */}
            {images.length > 0 && (
              <section className="mt-12 avoid-break">
                <div className="mb-4 border-b border-neutral-200 pb-2">
                  <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-neutral-500">Bilder fra befaring</h2>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {images.map((img) => (
                    <figure key={img.id} className="avoid-break overflow-hidden rounded-xl border border-neutral-200">
                      <img src={img.url} alt={img.caption ?? ""} className="h-56 w-full object-cover" />
                      {img.caption && (
                        <figcaption className="p-3 text-xs text-neutral-600">{img.caption}</figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              </section>
            )}

            {/* Vilkår og betingelser */}
            {termsBlocks.length > 0 && (
              <section className="mt-12 space-y-4">
                {termsBlocks.map((b) => (
                  <div key={b.title} className="avoid-break rounded-2xl border border-neutral-200 p-6">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
                      {b.title}
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">{b.body}</p>
                  </div>
                ))}
              </section>
            )}
            {legacyTerms && (
              <section className="avoid-break mt-12 rounded-2xl border border-neutral-200 p-6">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
                  Vilkår og forbehold
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">{legacyTerms}</p>
              </section>
            )}
            {quote.valid_until && (
              <div className="avoid-break mt-6 text-sm text-neutral-600">
                Tilbudet er gyldig til {formatDate(quote.valid_until)}.
              </div>
            )}

            {/* Signatur */}
            <section className="avoid-break mt-14">
              <div className="text-sm text-neutral-600">Med vennlig hilsen</div>
              {company.signature_url ? (
                <div className="mt-3 h-16">
                  <img
                    src={company.signature_url}
                    alt="Signatur"
                    className="h-full w-auto object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              ) : (
                <div className="mt-3 h-8" />
              )}
              <div className="mt-2 border-t border-neutral-300 pt-3 text-sm">
                <div className="font-semibold">{company.company_name}</div>
                <div className="text-neutral-600">Org.nr. {formatOrgNumber(company.org_number)}</div>
                <div className="mt-1 text-neutral-600">Telefon: {formatPhone(company.phone)}</div>
                <div className="text-neutral-600">{company.company_email}</div>
                <div className="text-neutral-600">{formatWebsite(company.website)}</div>
              </div>
            </section>

            {/* Signatur – begge parter */}
            <section className="avoid-break mt-12">
              <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
                Aksept av tilbud
              </div>
              <p className="mb-8 text-sm text-neutral-600">
                Ved signering aksepteres tilbudet med de vilkår og forbehold som er beskrevet over.
              </p>
              <div className="grid grid-cols-2 gap-10">
                <div>
                  <div className="h-10" />
                  <div className="border-t border-neutral-400 pt-2 text-xs text-neutral-600">
                    Sted og dato
                  </div>
                  <div className="mt-10 h-10" />
                  <div className="border-t border-neutral-400 pt-2 text-xs text-neutral-600">
                    For {company.company_name}
                  </div>
                </div>
                <div>
                  <div className="h-10" />
                  <div className="border-t border-neutral-400 pt-2 text-xs text-neutral-600">
                    Sted og dato
                  </div>
                  <div className="mt-10 h-10" />
                  <div className="border-t border-neutral-400 pt-2 text-xs text-neutral-600">
                    {customer?.name ? `For ${customer.name}` : "Kundens signatur"}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </article>
      </div>
    </>
  );
}

function CoverPage({
  company,
  quote,
  customer,
  accent,
  docLabel,
}: {
  company: {
    company_name: string;
    logo_url: string;
    org_number: string;
    phone: string;
    company_email: string;
    website: string;
  };
  docLabel: string;
  quote: { title: string; quote_number: string; revision_label: string | null; created_at: string; valid_until: string | null };
  customer: { name: string } | null;
  accent: string;
}) {
  return (
    <section
      className="page-break relative flex min-h-[297mm] flex-col justify-between px-14 py-16 print:min-h-[297mm]"
      style={{ background: `linear-gradient(160deg, ${accent}f0 0%, ${accent} 100%)`, color: "white" }}
    >
      <div className="flex items-center gap-4">
        <div className="grid h-32 w-72 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white/10 p-3 backdrop-blur">
          {company.logo_url && (
            <img
              src={company.logo_url}
              alt={company.company_name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
        </div>

        <div>
          <div className="text-xl font-semibold tracking-tight">{company.company_name}</div>
          <div className="text-sm text-white/70">Org.nr {formatOrgNumber(company.org_number)}</div>
        </div>
      </div>

      <div className="max-w-2xl">
        <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/70">{docLabel}</div>
        <h1 className="mt-4 text-5xl font-semibold leading-tight tracking-tight">{quote.title}</h1>
        {customer && (
          <div className="mt-8 text-lg text-white/90">Til {customer.name}</div>
        )}
        <div className="mt-2 font-mono text-sm text-white/70">
          {quote.quote_number}
          {quote.revision_label ? ` · Rev ${quote.revision_label}` : ""}
          {" · "}{formatDate(quote.created_at)}
          {quote.valid_until && <> · Gyldig til {formatDate(quote.valid_until)}</>}
        </div>
      </div>

      <div className="text-xs text-white/70">
        Telefon: {formatPhone(company.phone)}
      </div>
      <div className="text-xs text-white/70">
        {company.company_email} · {formatWebsite(company.website)}
      </div>
    </section>
  );
}

/** Fastpris: kun postnivå. Ingen mengder, ingen enhetspriser. */
function FixedPriceSections({ rows }: { rows: { title: string; sum: number }[] }) {
  return (
    <section className="avoid-break">
      <div className="mb-3 border-b border-neutral-200 pb-2">
        <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-neutral-500">Leveranse</h2>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.title} className="border-b border-neutral-100 align-top last:border-0">
              <td className="py-3 pr-3 font-medium">{r.title}</td>
              <td className="whitespace-nowrap py-3 text-right tabular-nums font-medium">{formatCurrency(r.sum)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ItemSection({
  title,
  items,
  muted,
  negative,
}: {
  title: string;
  items: QuoteItem[];
  muted?: boolean;
  negative?: boolean;
}) {
  return (
    <section className="avoid-break">
      <div className="mb-3 flex items-baseline justify-between border-b border-neutral-200 pb-2">
        <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-neutral-500">{title}</h2>
        <div className="text-xs text-neutral-400">
          {items.length} {items.length === 1 ? "post" : "poster"}
        </div>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {items.map((it, idx) => {
            const sign = negative ? -1 : 1;
            const sum = Number(it.quantity) * Number(it.unit_price) * sign;
            return (
              <tr
                key={idx}
                className={`border-b border-neutral-100 align-top last:border-0 ${muted ? "text-neutral-500" : ""}`}
              >
                <td className="py-2.5 pr-3">
                  <div className="font-medium">{it.description}</div>
                  {it.customer_note && (
                    <div className="mt-0.5 whitespace-pre-wrap text-xs text-neutral-500">{it.customer_note}</div>
                  )}
                </td>
                <td className="whitespace-nowrap py-2.5 pr-3 text-right tabular-nums text-neutral-500">
                  {formatQuantity(it.quantity, it.unit)} {it.unit}
                </td>
                <td className="whitespace-nowrap py-2.5 pr-3 text-right tabular-nums text-neutral-500">
                  {formatCurrency(it.unit_price)}
                </td>
                <td className="whitespace-nowrap py-2.5 text-right tabular-nums font-medium">
                  {formatCurrency(sum)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function SumRow({
  label,
  value,
  muted,
  large,
}: {
  label: string;
  value: string;
  muted?: boolean;
  large?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between ${large ? "text-lg font-semibold" : "text-sm"} ${
        muted ? "text-neutral-500" : ""
      } py-1`}
    >
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
